import { SxProp, View } from 'dripsy';
import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { useStore } from 'store/store';

import { CurrenciesContext } from '@/currencies/currency.context';
import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { Divider } from '@/design-system/components/Divider';
import { UniCheckbox } from '@/design-system/components/inputs';
import { H6, TextBody, TextCaption } from '@/design-system/components/typography';
import { formatDate } from '@/utils/dateFormatting';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { LayoutContext } from 'contexts/LayoutContext';
import { Action, AutoRenewalProps } from './AutoRenewal.web';
import { Icon } from '@/design-system/components/icons/Icon';
import { UseAutoRenewal } from './useAutoRenewal.hook';
import BottomSheet, { BottomSheetBackdrop, useBottomSheetDynamicSnapPoints } from '@gorhom/bottom-sheet';
import { BottomSheetHeader } from '@/design-system/components/headers';
import { CurrenciesIcon } from '../CurrenciesIcon';

export const AutoRenewalBodyWeb: React.FC<
  AutoRenewalProps & {
    autoRenewal: UseAutoRenewal;
    isSuccess: boolean;
    isAcknowledged: boolean;
    successTitle: string;
    toggleAcknowledge: () => void;
    handleConfirm: () => void;
    onBack: () => void;
    onSuccess: () => void;
    bottomSheetRef?: React.RefObject<BottomSheet>;
  }
> = ({
  subscription,
  autoRenewal,
  isAcknowledged,
  isSuccess,
  successTitle,
  toggleAcknowledge,
  handleConfirm,
  onBack,
  onSuccess,
  bottomSheetRef
}) => {
  const { t } = useTranslation('earn');
  const store = useStore();
  const layout = useContext(LayoutContext);
  const { getDecimalByAsset } = useContext(CurrenciesContext);

  console.log('subscription', subscription);

  const styles = layout.isMobile ? stylesNative : stylesWeb;

  const action = subscription.autoRenew ? Action.DISABLE : Action.ENABLE;

  const DisplayTexts: Record<Action, { title: string; description: string; acknowledgement: string }> = {
    [Action.ENABLE]: {
      title: t('earn-invest::enable-auto-renewal-confirmation.header--enable-confirmation'),
      description: t('earn-invest::enable-auto-renewal-confirmation.text--enable-reminder'),
      acknowledgement: t('earn-invest::enable-auto-renewal-confirmation.text--enable-statement', { vendor: store.brand })
    },
    [Action.DISABLE]: {
      title: t('earn-invest::disable-auto-renewal-confirmation.header--disable-confirmation'),
      description: t('earn-invest::disable-auto-renewal-confirmation.text--disable-reminder'),
      acknowledgement: t('earn-invest::disable-auto-renewal-confirmation.text--disable-statement', { vendor: store.brand })
    }
  };

  const TextStrings = DisplayTexts[action];

  if (layout.isMobile) {
    // Get dimensions
    const renderBackdrop = useCallback(
      (props) => (
        <BottomSheetBackdrop {...props} disappearsOnIndex={0} appearsOnIndex={1} pressBehavior={'none'} enableTouchThrough={true} />
      ),
      []
    );
    const { width, height } = layout.dimensions;
    const { animatedHandleHeight, animatedSnapPoints, animatedContentHeight, handleContentLayout } = useBottomSheetDynamicSnapPoints([
      'CONTENT_HEIGHT',
      'CONTENT_HEIGHT'
    ]);

    if (isSuccess)
      return (
        <View
          sx={{
            height: '100%',
            padding: '$7',
            gap: '$5',
            justifyContent: layout.isMobile ? 'space-between' : 'flex-start',
            backgroundColor: '$backgroundPrimary'
          }}>
          <View sx={{ gap: '$5' }}>
            <View sx={{ paddingY: '$5', alignItems: 'center', gap: '$3' }}>
              <Icon name="successSpecial" height={80} width={80} />
              <H6 bold>{successTitle}</H6>
            </View>
            <View sx={{ paddingY: '$5', gap: '$5' }}>
              <View>
                <TextCaption secondary label>
                  {t('earn-invest::glossary.subscription-amount')}
                </TextCaption>
                <H6 bold sx={{ color: '$secondary' }}>
                  {formatAmountByDecimals({
                    value: subscription.amount,
                    decimals: getDecimalByAsset(subscription.asset),
                    suffix: subscription.asset
                  })}
                </H6>
              </View>
              <View sx={{ gap: '$3' }}>
                <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <TextBody secondary>{t('earn-invest::glossary.maturity')}</TextBody>
                  <TextBody>{formatDate(subscription.maturityDate)}</TextBody>
                </View>
                <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <TextBody secondary>{t('earn-invest::glossary.instruction-time')}</TextBody>
                  <TextBody>{formatDate(autoRenewal.mutate.data?.data.instructionTime)}</TextBody>
                </View>
                <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <TextBody secondary>{t('earn-invest::glossary.ref-id')}</TextBody>
                  <TextBody>{autoRenewal.mutate.data?.data.refId || ' - '}</TextBody>
                </View>
              </View>
            </View>
          </View>
          <UniButton size="default" onPress={onSuccess} label={t('earn-invest::glossary.back-to-overview')} />
        </View>
      );

    // Bottom Sheet on mobile
    return (
      <View sx={{ width, height, backgroundColor: 'transparent' }}>
        <BottomSheet
          index={0}
          ref={bottomSheetRef}
          snapPoints={animatedSnapPoints}
          handleHeight={animatedHandleHeight}
          contentHeight={animatedContentHeight}
          backdropComponent={renderBackdrop}
          handleComponent={() => <View sx={styles.bottomsheetHeadHandler} />}>
          <View sx={{ height: 'auto', width, backgroundColor: '$backgroundPrimary' }} onLayout={handleContentLayout}>
            <BottomSheetHeader
              title={t('earn-invest::subscription-confirmation.header--subscription-confirmation')}
              onPress={() => {
                bottomSheetRef?.current?.close();
                setTimeout(() => {
                  onBack();
                }, 300);
              }}
              sx={{ paddingX: '$4', pb: 0 }}
            />
            <View sx={styles.modalBody}>
              <TextBody secondary={!layout.isMobile} semiBold={layout.isMobile}>
                {TextStrings.description}
              </TextBody>
              <View sx={{ height: '$5' }} />
              <View>
                <TextCaption secondary label>
                  {t('earn-invest::glossary.subscription-amount')}
                </TextCaption>
                <H6 bold sx={{ color: '$secondary' }}>
                  {formatAmountByDecimals({
                    value: subscription.amount,
                    decimals: getDecimalByAsset(subscription.amount),
                    suffix: subscription.asset
                  })}
                </H6>
              </View>
              <View sx={{ height: '$5' }} />
              <ModalBodyDataRow label={t('earn-invest::glossary.maturity')} value={formatDate(subscription.maturityDate)} />
              <Divider sx={{ marginY: '$5' }} />
              <UniCheckbox
                label={TextStrings.acknowledgement}
                value={isAcknowledged}
                onValueChange={toggleAcknowledge}
                options={{ labelSecondaryColor: true }}
              />
              <View sx={styles.buttonGroup}>
                {!layout.isMobile && (
                  <UniButton
                    size="default"
                    variant={ButtonVariant.outline1}
                    label={t('earn-invest::glossary.cancel')}
                    onPress={onBack}
                    wrapperSx={styles.button}
                  />
                )}
                <UniButton
                  size="default"
                  variant={ButtonVariant.primary}
                  label={t('earn-invest::glossary.place-instruction')}
                  onPress={handleConfirm}
                  wrapperSx={styles.button}
                  disabled={!isAcknowledged}
                />
              </View>
            </View>
          </View>
        </BottomSheet>
      </View>
    );
  }

  if (isSuccess)
    return (
      <View
        sx={{
          height: '100%',
          padding: '$7',
          gap: '$5',
          justifyContent: layout.isMobile ? 'space-between' : 'flex-start'
        }}>
        <View sx={{ gap: '$5' }}>
          <View sx={{ paddingY: '$5', alignItems: 'center', gap: '$3' }}>
            <Icon name="successSpecial" height={80} width={80} />
            <H6 bold>{successTitle}</H6>
          </View>
          <CurrenciesIcon asset={subscription.asset} title={subscription.offerName} />
          <View sx={{ paddingY: '$5', gap: '$5' }}>
            <View>
              <TextCaption secondary label semiBold>
                {t('earn-invest::glossary.subscription-amount')}
              </TextCaption>
              <H6 bold sx={{ color: '$secondary' }}>
                {formatAmountByDecimals({
                  value: subscription.amount,
                  decimals: getDecimalByAsset(subscription.asset),
                  suffix: subscription.asset
                })}
              </H6>
            </View>
            <View sx={{ gap: '$3' }}>
              <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <TextBody secondary>{t('earn-invest::glossary.maturity')}</TextBody>
                <TextBody>{formatDate(subscription.maturityDate)}</TextBody>
              </View>
              <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <TextBody secondary>{t('earn-invest::glossary.instruction-time')}</TextBody>
                <TextBody>{formatDate(autoRenewal.mutate.data?.data.instructionTime)}</TextBody>
              </View>
              <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <TextBody secondary>{t('earn-invest::glossary.ref-id')}</TextBody>
                <TextBody>{autoRenewal.mutate.data?.data.refId || ' - '}</TextBody>
              </View>
            </View>
          </View>
        </View>
        <UniButton size="default" onPress={onSuccess} label={t('earn-invest::glossary.back-to-overview')} />
      </View>
    );

  return (
    <View sx={{}}>
      <View sx={styles.modalBody}>
        <TextBody secondary={!layout.isMobile} semiBold>
          {TextStrings.description}
        </TextBody>
        <View sx={{ height: '$5' }} />
        <CurrenciesIcon asset={subscription.asset} title={subscription.offerName} />
        <View sx={{ height: '$5' }} />
        <View>
          <TextCaption secondary label>
            {t('earn-invest::glossary.subscription-amount')}
          </TextCaption>
          <H6 bold sx={{ color: '$secondary' }}>
            {formatAmountByDecimals({
              value: subscription.amount,
              decimals: getDecimalByAsset(subscription.amount),
              suffix: subscription.asset
            })}
          </H6>
        </View>
        <View sx={{ height: '$5' }} />
        <ModalBodyDataRow label={t('earn-invest::glossary.maturity')} value={formatDate(subscription.maturityDate)} />
        <View sx={{ height: '$5' }} />
        <UniCheckbox
          label={TextStrings.acknowledgement}
          value={isAcknowledged}
          onValueChange={toggleAcknowledge}
          options={{ labelSecondaryColor: true }}
        />
        <View sx={styles.buttonGroup}>
          {!layout.isMobile && (
            <UniButton
              size="default"
              variant={ButtonVariant.outline1}
              label={t('earn-invest::glossary.cancel')}
              onPress={onBack}
              wrapperSx={styles.button}
            />
          )}
          <UniButton
            size="default"
            variant={ButtonVariant.primary}
            label={t('earn-invest::glossary.place-instruction')}
            onPress={handleConfirm}
            wrapperSx={styles.button}
            disabled={!isAcknowledged}
          />
        </View>
      </View>
    </View>
  );
};

const ModalBodyRow: React.FC<{ sx?: SxProp }> = ({ sx, children }) => {
  const layout = useContext(LayoutContext);
  const styles = layout.isMobile ? stylesNative : stylesWeb;

  return <View sx={{ ...styles.modalBodyRow, ...sx }}>{children}</View>;
};

const ModalBodyDataRow: React.FC<{ sx?: SxProp; label: string; value: string }> = ({ label, value, sx, children }) => {
  return (
    <ModalBodyRow sx={sx}>
      <TextBody secondary>{label}</TextBody>
      <TextBody>{value}</TextBody>
    </ModalBodyRow>
  );
};

enum StyleName {
  container = 'container',
  modalBody = 'modalBody',
  modalBodyRow = 'modalBodyRow',
  amountBox = 'amountBox',
  buttonGroup = 'buttonGroup',
  button = 'button',
  bottomsheetHeadHandler = 'bottomsheetHeadHandler'
}

const stylesWeb: Record<StyleName, SxProp> = {
  container: {
    width: '100%',
    maxWidth: 500
  },
  modalBody: {
    padding: '$7'
  },
  modalBodyRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  amountBox: {},
  buttonGroup: {
    flex: 1,
    mt: '$10',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: '$3'
  },
  button: {
    flexGrow: 1
  }
};

const stylesNative: Record<StyleName, SxProp> = {
  container: {
    backgroundColor: '$backgroundPrimary'
  },

  modalBody: {
    padding: '$4'
  },
  modalBodyRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  amountBox: {},
  buttonGroup: {
    flex: 1,
    mt: '$10',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: '$3'
  },
  button: {
    flexGrow: 1
  },
  bottomsheetHeadHandler: {
    height: '$5',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    backgroundColor: '$backgroundPrimary',
    borderWidth: 0
  }
};
